import type { RouteIssue } from "./routes.js";

export function LoadingScreen() {
  return (
    <main className="centered-screen" aria-busy="true">
      <span className="brand-mark large" aria-hidden="true">
        P
      </span>
      <p className="eyebrow">POLYFUTURE · OPENING WORKSPACE</p>
      <h1>Loading governed risk state…</h1>
      <div className="loading-line" aria-hidden="true" />
    </main>
  );
}

export function SignInBoundary({ onRetry }: { onRetry: () => void }) {
  return (
    <main className="centered-screen auth-boundary">
      <span className="brand-mark large" aria-hidden="true">
        P
      </span>
      <p className="eyebrow">POLYFUTURE · SESSION REQUIRED</p>
      <h1>Your organization session has ended.</h1>
      <p>
        Sign in through your organization’s configured Identity Platform entry point, then retry.
        Your requested Polyfuture destination is preserved in this browser.
      </p>
      <button className="button button-primary" type="button" onClick={onRetry}>
        Check session again
      </button>
    </main>
  );
}

export function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main className="centered-screen" role="alert">
      <p className="eyebrow">WORKSPACE UNAVAILABLE</p>
      <h1>Polyfuture could not load safely.</h1>
      <p>{message}</p>
      <button className="button button-primary" type="button" onClick={onRetry}>
        Retry
      </button>
    </main>
  );
}

export function WorkspaceLoading() {
  return (
    <div
      className="workspace-loading"
      role="status"
      aria-busy="true"
      aria-label="Loading selected portfolio"
    >
      <div />
      <div />
      <div />
    </div>
  );
}

export function EmptyCatalog() {
  return (
    <section className="empty-state full">
      <p className="eyebrow">No portfolios available</p>
      <h1>A governed portfolio must be configured before data intake.</h1>
      <p>
        Ask an organization administrator to bind a profile and effective configuration. The
        portfolio will then appear here for controlled import and what-if work.
      </p>
    </section>
  );
}

export function RouteUnavailable({
  issue,
  onReturnToday,
}: {
  issue: RouteIssue;
  onReturnToday: () => void;
}) {
  const copy = routeIssueCopy(issue);
  return (
    <section className="empty-state full route-unavailable" role="alert">
      <p className="eyebrow">{copy.eyebrow}</p>
      <h1>{copy.title}</h1>
      <p>{copy.body}</p>
      <button className="button button-primary" type="button" onClick={onReturnToday}>
        Return to Today
      </button>
    </section>
  );
}

function routeIssueCopy(issue: RouteIssue) {
  switch (issue) {
    case "permission_denied":
      return {
        eyebrow: "PERMISSION-LIMITED VIEW",
        title: "This area is not available to your role.",
        body: "Polyfuture checked your current organization membership on the server. Ask an administrator if your responsibilities require access.",
      };
    case "book_not_found":
      return {
        eyebrow: "Portfolio unavailable",
        title: "This governed portfolio cannot be opened.",
        body: "It may not exist, may have been retired, or may belong to an organization you cannot access. Polyfuture does not reveal records outside your authorized catalog.",
      };
    case "not_found":
      return {
        eyebrow: "DESTINATION NOT FOUND",
        title: "This route is not part of the current product.",
        body: "Use Today to return to the authorized organization workspace. Planned prototype screens are not exposed before their production contracts exist.",
      };
  }
}
